import LoadingButton from '@mui/lab/LoadingButton';
import {
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { SextforceAutoRepostWithLatestRepostLog } from '../../../../hooks/useSextforceAutoRepost';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type BulkMode = 'pause' | 'resume';

type Props = {
    fetchActiveAutoReposts: () => Promise<SextforceAutoRepostWithLatestRepostLog[]>;
    refetchAutoReposts: () => void;
    bulkSetAutoRepostInactiveStatus: (autoRepostIds: string[], inactive: boolean) => Promise<{ success: boolean }>;
    bulkSetAutoRepostInactiveStatusLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'repostCount' | 'nextRepostDate';
type SortDirection = 'asc' | 'desc';

const SextforceAutoRepostBulkPauseDialog = (props: Props) => {
    const {
        fetchActiveAutoReposts,
        refetchAutoReposts,
        bulkSetAutoRepostInactiveStatus,
        bulkSetAutoRepostInactiveStatusLoading,
        open,
        setOpen,
    } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [mode, setMode] = useState<BulkMode>('pause');
    const [autoReposts, setAutoReposts] = useState<SextforceAutoRepostWithLatestRepostLog[]>([]);
    const [loadingAutoReposts, setLoadingAutoReposts] = useState(false);
    const [selectedAutoRepostsIds, setSelectedAutoRepostsIds] = useState<string[]>([]);
    const [selectedAutoReposts, setSelectedAutoReposts] = useState<SextforceAutoRepostWithLatestRepostLog[]>([]);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const isPauseMode = mode === 'pause';
    const accentColor = isPauseMode ? 'warning' : 'success';

    useEffect(() => {
        if (!open) {
            return;
        }

        setLoadingAutoReposts(true);

        fetchActiveAutoReposts()
            .then(reposts => {
                setAutoReposts(reposts);
            })
            .finally(() => {
                setLoadingAutoReposts(false);
            });
    }, [open, fetchActiveAutoReposts]);

    useEffect(() => {
        setSelectedAutoRepostsIds([]);
        setSelectedAutoReposts([]);
    }, [mode]);

    const eligibleAutoReposts = useMemo(
        () =>
            autoReposts.filter(autoRepost =>
                isPauseMode ? autoRepost.active && !autoRepost.inactive : autoRepost.active && autoRepost.inactive,
            ),
        [autoReposts, isPauseMode],
    );

    const sortedAutoReposts = useMemo(() => {
        return [...eligibleAutoReposts].sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'repostCount':
                    aValue = a.repostCount;
                    bValue = b.repostCount;
                    break;
                case 'nextRepostDate':
                    aValue = new Date(a.nextRepostDate).getTime();
                    bValue = new Date(b.nextRepostDate).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [eligibleAutoReposts, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        setMode('pause');
        setAutoReposts([]);
        setSelectedAutoReposts([]);
        setSelectedAutoRepostsIds([]);
        setOpen(false);
    };

    const handleSubmit = () => {
        const actionLabel = isPauseMode ? 'pause' : 'resume';

        dialog
            .confirm({
                title: isPauseMode ? 'Pause Auto Reposts' : 'Resume Auto Reposts',
                message: isPauseMode
                    ? `Are you sure you want to pause ${selectedAutoReposts.length.toLocaleString()} Auto Reposts? They will stay in your active list and skip reposts until resumed.`
                    : `Are you sure you want to resume ${selectedAutoReposts.length.toLocaleString()} Auto Reposts? They will return to their normal repost schedule.`,
                ok: {
                    text: isPauseMode ? 'Pause' : 'Resume',
                    color: accentColor,
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                bulkSetAutoRepostInactiveStatus(selectedAutoRepostsIds, isPauseMode)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(`Auto Reposts ${actionLabel}d successfully`, { variant: 'success' });
                            refetchAutoReposts();
                            handleClose();
                        } else {
                            enqueueSnackbar(`Failed to ${actionLabel} Auto Reposts`, { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar(`Failed to ${actionLabel} Auto Reposts`, { variant: 'error' });
                    });
            })
            .catch(() => {});
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title="Auto Reposts Bulk Pause / Resume" />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" pl={2} pr={2}>
                        <ToggleButtonGroup
                            color={accentColor}
                            value={mode}
                            exclusive
                            size="small"
                            onChange={(_event, value: BulkMode | null) => {
                                if (value !== null) {
                                    setMode(value);
                                }
                            }}
                        >
                            <ToggleButton value="pause">Pause</ToggleButton>
                            <ToggleButton value="resume">Resume</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        {isPauseMode
                            ? 'Select the active Auto Reposts you want to pause. Paused reposts remain in your active list and skip processing until resumed.'
                            : 'Select the paused Auto Reposts you want to resume. They will return to their normal repost schedule.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedAutoReposts.length.toLocaleString()} Auto Reposts selected
                    </Typography>

                    {loadingAutoReposts ? (
                        <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                            Loading active Auto Reposts...
                        </Typography>
                    ) : sortedAutoReposts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                            {isPauseMode ? 'No active Auto Reposts available to pause.' : 'No paused Auto Reposts available to resume.'}
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                backgroundColor: theme => alpha(theme.palette[accentColor].main, 0.1),
                            }}
                        >
                            <TableContainer
                                sx={{
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        padding: '4px 8px',
                                    },
                                    '& .MuiTableCell-root:first-of-type': {
                                        paddingLeft: 0,
                                    },
                                    '& .MuiTableCell-root:last-of-type': {
                                        paddingRight: 2,
                                    },
                                }}
                            >
                                <Table
                                    size="small"
                                    sx={{
                                        '& thead th': {
                                            position: 'sticky',
                                            top: 0,
                                            backgroundColor: isPauseMode ? 'rgb(239, 228, 198)' : 'rgb(214, 235, 214)',
                                            zIndex: 1,
                                        },
                                        '& th:first-of-type, & td:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1,
                                        },
                                    }}
                                >
                                    <TableHead
                                        sx={{
                                            backgroundColor: isPauseMode ? 'rgb(239, 228, 198)' : 'rgb(214, 235, 214)',
                                            '& th:first-of-type': {
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 2,
                                            },
                                        }}
                                    >
                                        <TableRow
                                            sx={{
                                                '& th': {
                                                    backgroundColor: theme => theme.palette.background.paper,
                                                    color: theme => theme.palette.text.primary,
                                                    fontWeight: 'bold',
                                                },
                                            }}
                                        >
                                            <TableCell width={50} align="center">
                                                <Checkbox
                                                    indeterminate={
                                                        selectedAutoRepostsIds.length > 0 &&
                                                        selectedAutoRepostsIds.length < sortedAutoReposts.length
                                                    }
                                                    checked={
                                                        sortedAutoReposts.length > 0 &&
                                                        selectedAutoReposts.length === sortedAutoReposts.length
                                                    }
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        if (e.target.checked) {
                                                            setSelectedAutoRepostsIds(sortedAutoReposts.map(autoRepost => autoRepost._id!));
                                                            setSelectedAutoReposts(sortedAutoReposts);
                                                        } else {
                                                            setSelectedAutoRepostsIds([]);
                                                            setSelectedAutoReposts([]);
                                                        }
                                                    }}
                                                    color={accentColor}
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'name'}
                                                    direction={sortField === 'name' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('name')}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                >
                                                    Auto Repost
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="center" width={50}>
                                                <TableSortLabel
                                                    active={sortField === 'repostCount'}
                                                    direction={sortField === 'repostCount' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('repostCount')}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                >
                                                    Reposts
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell width={150}>
                                                <TableSortLabel
                                                    active={sortField === 'nextRepostDate'}
                                                    direction={sortField === 'nextRepostDate' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('nextRepostDate')}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                >
                                                    Next Repost
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {sortedAutoReposts.map(autoRepost => (
                                            <TableRow
                                                key={autoRepost._id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                    backgroundColor: theme =>
                                                        selectedAutoRepostsIds.includes(autoRepost._id!)
                                                            ? alpha(theme.palette[accentColor].main, 0.2)
                                                            : 'transparent',
                                                }}
                                            >
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={selectedAutoRepostsIds.includes(autoRepost._id!)}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.checked) {
                                                                setSelectedAutoRepostsIds([...selectedAutoRepostsIds, autoRepost._id!]);
                                                                setSelectedAutoReposts([...selectedAutoReposts, autoRepost]);
                                                            } else {
                                                                setSelectedAutoRepostsIds(
                                                                    selectedAutoRepostsIds.filter(id => id !== autoRepost._id),
                                                                );
                                                                setSelectedAutoReposts(
                                                                    selectedAutoReposts.filter(ar => ar._id !== autoRepost._id),
                                                                );
                                                            }
                                                        }}
                                                        color={accentColor}
                                                        size="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.primary" noWrap>
                                                        {autoRepost.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {autoRepost.repostCount.toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {moment(autoRepost.nextRepostDate).format('DD/MM/YYYY hh:mm A')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color={accentColor}
                    size="small"
                    loading={bulkSetAutoRepostInactiveStatusLoading}
                    disabled={selectedAutoRepostsIds.length === 0}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <span>{isPauseMode ? 'Pause Selected' : 'Resume Selected'}</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default SextforceAutoRepostBulkPauseDialog;
